import { useEffect, useState } from "react";
import { Phone } from "lucide-react";
import { motion } from "framer-motion";
import { CONTACT, scrollToId } from "@/lib/api";
import { useAvailability } from "@/hooks/useAvailability";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const { data } = useAvailability();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
        scrolled ? "bg-white/90 backdrop-blur-xl shadow-lg shadow-navy/5" : "bg-transparent"
      }`}
    >
      <div data-testid="alert-bar" className="bg-tpink text-white">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 px-5 py-1.5 text-[11px] sm:text-xs font-bold tracking-wide uppercase">
          <span>Drip alert: mystery water bills don't solve themselves.</span>
          <button data-testid="alert-bar-cta" onClick={() => scrollToId("book")} className="underline underline-offset-2 hover:text-navy transition-colors duration-200 whitespace-nowrap">
            Grab a live slot →
          </button>
        </div>
      </div>
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 px-5 sm:px-8 py-3">
        <a href="/" data-testid="nav-logo-link" className="flex items-center gap-3">
          <img src={CONTACT.logo} alt="The Plumbing & Electrical Co. logo" className="w-12 h-12 sm:w-14 sm:h-14 object-contain" />
          <span className="hidden sm:block font-bebas text-xl leading-tight tracking-wide text-navy">
            LEAK-DETECTION<span className="text-brand">.COM.AU</span>
            <span className="block text-[10px] font-manrope font-bold tracking-[0.2em] text-navy/50">
              BY THE PLUMBING & ELECTRICAL CO.
            </span>
          </span>
        </a>
        <span className="hidden lg:block font-bold text-[11px] xl:text-xs tracking-[0.22em] text-brand uppercase whitespace-nowrap" data-testid="nav-eyebrow">
          Perth · Water & Gas Leak Detection
        </span>
        <div className="flex items-center gap-2 sm:gap-3">
          {data?.next_available && (
            <motion.button
              data-testid="nav-next-slot-pill"
              onClick={() => scrollToId("book")}
              animate={{
                scale: [1, 1.06, 1],
                boxShadow: [
                  "0 0 0 0 rgba(173,255,1,0.55)",
                  "0 0 0 12px rgba(173,255,1,0)",
                  "0 0 0 0 rgba(173,255,1,0)",
                ],
              }}
              transition={{ duration: 1.7, repeat: Infinity, ease: "easeOut" }}
              className="hidden md:flex items-center gap-2 rounded-full bg-navy text-white text-sm font-bold px-6 py-3 hover:bg-navy-light transition-colors duration-300"
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-tlime opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-tlime" />
              </span>
              Next available:
              <motion.span
                className="text-tlime font-extrabold uppercase"
                animate={{ scale: [1, 1.14, 1], opacity: [1, 0.55, 1] }}
                transition={{ duration: 1.7, repeat: Infinity, ease: "easeInOut" }}
              >
                {data.next_available.label}
              </motion.span>
            </motion.button>
          )}
          <a
            data-testid="nav-call-button"
            href={CONTACT.phoneHref}
            className="flex items-center gap-2 rounded-full bg-brand text-white font-bebas text-lg tracking-wide px-5 py-2.5 hover:bg-brand-hover hover:-translate-y-0.5 hover:shadow-xl hover:shadow-brand/30 transition-[transform,box-shadow,background-color] duration-300"
          >
            <Phone className="w-4 h-4" />
            {CONTACT.phone}
          </a>
        </div>
      </div>
    </header>
  );
};
